import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import api from '@/services/api';

/** 12 màu preset cho thẻ đơn hàng — phải khớp ORDER_TAG_COLORS ở BE (order-tag.schema.ts). */
export const ORDER_TAG_COLORS = [
  '#91d5ff',
  '#ffe58f',
  '#ffe7ba',
  '#13a8a8',
  '#b7eb8f',
  '#ffd6e7',
  '#cf1322',
  '#d9d9d9',
  '#b37feb',
  '#ff4d4f',
  '#fa8c16',
  '#9e1068',
];

export interface OrderTag {
  _id: string;
  name: string;
  color: string;
}

/** Chữ đen trên nền sáng, chữ trắng trên nền tối — theo độ sáng của màu nền. */
export function textColorFor(bg: string): string {
  const hex = bg.replace('#', '');
  const r = parseInt(hex.slice(0, 2), 16);
  const g = parseInt(hex.slice(2, 4), 16);
  const b = parseInt(hex.slice(4, 6), 16);
  const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
  return luminance > 0.6 ? '#000' : '#fff';
}

export interface AssignOrderTagsPayload {
  orderId: string;
  add_tag_ids?: string[];
  remove_tag_ids?: string[];
}

export const orderTagsApi = {
  list: async (): Promise<OrderTag[]> => {
    const { data } = await api.get('/orders/tags');
    return data.data as OrderTag[];
  },
  create: async (payload: { name: string; color: string }): Promise<OrderTag> => {
    const { data } = await api.post('/orders/tags', payload);
    return data as OrderTag;
  },
  update: async (id: string, payload: { name?: string; color?: string }): Promise<OrderTag> => {
    const { data } = await api.patch(`/orders/tags/${id}`, payload);
    return data as OrderTag;
  },
  remove: async (id: string): Promise<{ deleted: boolean }> => {
    const { data } = await api.delete(`/orders/tags/${id}`);
    return data as { deleted: boolean };
  },
  assign: async ({ orderId, ...payload }: AssignOrderTagsPayload) => {
    const { data } = await api.post(`/orders/${orderId}/tags`, payload);
    return data;
  },
};

export const useOrderTags = () => useQuery({ queryKey: ['order-tags'], queryFn: orderTagsApi.list });

export const useCreateOrderTag = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: orderTagsApi.create,
    onSuccess: () => qc.invalidateQueries({ queryKey: ['order-tags'] }),
  });
};

export const useUpdateOrderTag = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, payload }: { id: string; payload: { name?: string; color?: string } }) =>
      orderTagsApi.update(id, payload),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['order-tags'] });
      qc.invalidateQueries({ queryKey: ['orders'] });
    },
  });
};

export const useDeleteOrderTag = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: orderTagsApi.remove,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['order-tags'] });
      qc.invalidateQueries({ queryKey: ['orders'] });
    },
  });
};

export const useAssignOrderTags = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: orderTagsApi.assign,
    onSuccess: () => qc.invalidateQueries({ queryKey: ['orders'] }),
  });
};
